import React, { useState } from 'react';
import { SearchIcon, ClipboardIcon, WarningIcon, GoalIcon, TipIcon } from '../../../icons';
import { WorkstyleProfile, WorkstyleScores, WorkstyleDimension } from './types';
import { WORKSTYLE_QUESTIONS, WORKSTYLE_INTERPRETATIONS, getWorkstyleGuidance } from './workstyleQuestions';

interface WorkstyleAssessmentProps {
  onComplete: (profile: WorkstyleProfile) => void;
  onCancel: () => void;
}

/**
 * Workstyle Assessment Component
 * 
 * 8 vragen met sliders die meten HOE je werkt (0-10 per dimensie).
 * Kolbe-geïnspireerd model.
 */
const WorkstyleAssessment: React.FC<WorkstyleAssessmentProps> = ({
  onComplete,
  onCancel
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [scores, setScores] = useState<Partial<Record<WorkstyleDimension, number>>>({});
  const [showResults, setShowResults] = useState(false);
  const [profile, setProfile] = useState<WorkstyleProfile | null>(null);

  const progress = ((currentQuestion + 1) / WORKSTYLE_QUESTIONS.length) * 100;
  const question = WORKSTYLE_QUESTIONS[currentQuestion];
  const currentScore = scores[question.dimension] ?? 5; // Default middle

  // Handle slider change
  const handleScoreChange = (value: number) => {
    setScores(prev => ({
      ...prev,
      [question.dimension]: value
    }));
  };

  // Go to next question
  const handleNext = () => {
    if (currentQuestion < WORKSTYLE_QUESTIONS.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      // All questions answered, calculate profile
      const calculatedProfile = calculateProfile(scores as WorkstyleScores);
      setProfile(calculatedProfile);
      setShowResults(true);
    }
  };

  // Go back
  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  // Calculate average score per dimension
  const calculateDimensionScore = (dimension: WorkstyleDimension, answers: Partial<Record<WorkstyleDimension, number>>): number => {
    const questionsForDimension = WORKSTYLE_QUESTIONS.filter(q => q.dimension === dimension);
    const scoresForDimension = questionsForDimension.map(q => answers[q.dimension] ?? 5);
    return Math.round(scoresForDimension.reduce((a, b) => a + b, 0) / scoresForDimension.length);
  };

  // Calculate full profile
  const calculateProfile = (answers: WorkstyleScores): WorkstyleProfile => {
    const dimensions: WorkstyleDimension[] = ['fact_finder', 'follow_thru', 'quick_start', 'implementor'];
    
    const finalScores: WorkstyleScores = {
      fact_finder: calculateDimensionScore('fact_finder', answers),
      follow_thru: calculateDimensionScore('follow_thru', answers),
      quick_start: calculateDimensionScore('quick_start', answers),
      implementor: calculateDimensionScore('implementor', answers)
    };

    // Find primary strength and weakness
    const sortedDimensions = dimensions
      .map(d => ({ dimension: d, score: finalScores[d] }))
      .sort((a, b) => b.score - a.score);

    const primaryStrength = sortedDimensions[0].dimension;
    const primaryWeakness = sortedDimensions[sortedDimensions.length - 1].dimension;

    // Get guidance
    const guidance = getWorkstyleGuidance(finalScores);

    return {
      scores: finalScores,
      primaryStrength,
      primaryWeakness,
      energyGivers: guidance.energyGivers,
      energyDrainers: guidance.energyDrainers,
      idealTasks: guidance.idealTasks,
      avoidTasks: guidance.avoidTasks,
      completedAt: Date.now()
    };
  };

  // Get interpretation for score
  const getInterpretation = (dimension: WorkstyleDimension, score: number) => {
    const interpretations = WORKSTYLE_INTERPRETATIONS[dimension];
    if (score <= 3) return interpretations.low;
    if (score >= 7) return interpretations.high;
    return interpretations.medium;
  };

  // Results view
  if (showResults && profile) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90dvh] overflow-y-auto scrollbar-always">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-6 text-white">
            <h2 className="text-2xl font-bold mb-2">⚡ Jouw Werkstijl Profiel</h2>
            <p className="text-indigo-100">
              Ontdek hoe je het beste werkt en waar je energie van krijgt
            </p>
          </div>

          <div className="p-6 space-y-6">
            {/* Scores Overview */}
            <div className="grid md:grid-cols-2 gap-4">
              {(['fact_finder', 'follow_thru', 'quick_start', 'implementor'] as WorkstyleDimension[]).map(dimension => {
                const score = profile.scores[dimension];
                const interpretation = getInterpretation(dimension, score);
                const isPrimary = dimension === profile.primaryStrength;
                const isWeakness = dimension === profile.primaryWeakness;

                return (
                  <div
                    key={dimension}
                    className={`p-4 rounded-lg border-2 ${isPrimary ? 'bg-green-50 border-green-300' : isWeakness ? 'bg-orange-50 border-orange-300' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">
                        {dimension === 'fact_finder' && <><SearchIcon size={14} className="inline" /> Fact Finder</>}
                        {dimension === 'follow_thru' && <><ClipboardIcon size={14} className="inline" /> Follow Through</>}
                        {dimension === 'quick_start' && '⚡ Quick Start'}
                        {dimension === 'implementor' && '🔨 Implementor'}
                      </h4>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-gray-900">{score}/10</span>
                        {isPrimary && <span className="text-sm px-2 py-0.5 bg-green-200 text-green-800 rounded-full font-medium">Kracht</span>}
                        {isWeakness && <span className="text-sm px-2 py-0.5 bg-orange-200 text-orange-800 rounded-full font-medium">Let op</span>}
                      </div>
                    </div>
                    
                    {/* Score bar */}
                    <div className="mb-3">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full transition-all ${isPrimary ? 'bg-green-500' : isWeakness ? 'bg-orange-500' : 'bg-indigo-500'}`}
                          style={{ width: `${score * 10}%` }}
                        />
                      </div>
                    </div>

                    <p className="text-sm font-medium text-gray-700 mb-1">{interpretation.label}</p>
                    <p className="text-xs text-gray-600">{interpretation.description}</p>
                  </div>
                );
              })}
            </div>

            {/* Energy Givers & Drainers */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Energy Givers */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                  <span>🔋</span>
                  <span>Energie Gevers</span>
                </h4>
                <ul className="space-y-2">
                  {profile.energyGivers.map((item, idx) => (
                    <li key={idx} className="text-sm text-green-800 flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Energy Drainers */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                  <WarningIcon size={16} />
                  <span>Energie Slurpers</span>
                </h4>
                <ul className="space-y-2">
                  {profile.energyDrainers.map((item, idx) => (
                    <li key={idx} className="text-sm text-red-800 flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">×</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Ideal vs Avoid Tasks */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Ideal Tasks */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-3"><GoalIcon size={16} className="inline" /> Ideale Taken</h4>
                <ul className="space-y-1.5">
                  {profile.idealTasks.map((task, idx) => (
                    <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                      <span className="text-blue-500">•</span>
                      <span>{task}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Avoid Tasks */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-900 mb-3"><TipIcon size={16} className="inline" /> Delegeer Dit</h4>
                <ul className="space-y-1.5">
                  {profile.avoidTasks.map((task, idx) => (
                    <li key={idx} className="text-sm text-purple-800 flex items-start gap-2">
                      <span className="text-purple-500">•</span>
                      <span>{task}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={onCancel}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium"
              >
                ← Terug
              </button>
              <button
                onClick={() => onComplete(profile)}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg text-lg font-bold hover:from-indigo-600 hover:to-purple-600 transition-all shadow-md hover:shadow-lg"
              >
                ✓ Profiel Opslaan & Verder →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Question view with slider
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-4 text-white">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold">⚡ Werkstijl Assessment</h2>
            <span className="text-sm bg-white/20 px-3 py-1 rounded-full">
              Vraag {currentQuestion + 1} / {WORKSTYLE_QUESTIONS.length}
            </span>
          </div>
          {/* Progress bar */}
          <div className="w-full bg-indigo-300/30 rounded-full h-2">
            <div
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="p-8">
          <div className="mb-8">
            <p className="text-lg text-gray-900 font-medium mb-1">{question.question}</p>
            <p className="text-sm text-gray-500">Versleep de slider naar waar jij bent</p>
          </div>

          {/* Slider */}
          <div className="space-y-6">
            {/* Labels */}
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 max-w-[45%]">{question.leftLabel}</span>
              <span className="text-gray-600 max-w-[45%] text-right">{question.rightLabel}</span>
            </div>

            {/* Slider */}
            <div className="relative px-2">
              <input
                type="range"
                min="0"
                max="10"
                value={currentScore}
                onChange={(e) => handleScoreChange(Number(e.target.value))}
                className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, #6366f1 0%, #6366f1 ${currentScore * 10}%, #e5e7eb ${currentScore * 10}%, #e5e7eb 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
                  <span key={i}>{i}</span>
                ))}
              </div>
            </div>

            {/* Current score */}
            <div className="text-center">
              <div className="inline-block px-6 py-3 bg-indigo-100 rounded-lg">
                <p className="text-sm text-indigo-700 mb-1">Jouw score</p>
                <p className="text-3xl font-bold text-indigo-900">{currentScore}/10</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handleBack}
              disabled={currentQuestion === 0}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Vorige
            </button>
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-400 hover:text-gray-600 text-sm"
            >
              Annuleren
            </button>
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
            >
              {currentQuestion === WORKSTYLE_QUESTIONS.length - 1 ? 'Resultaten →' : 'Volgende →'}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #6366f1;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        .slider::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #6366f1;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
};

export default WorkstyleAssessment;
